"use client"

import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  ReactNode,
  ComponentPropsWithoutRef,
} from "react";
import Link from "next/link";
import { ChevronLeft, FilterIcon, X } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import ProductCard from "@/components/shop/product-card";
import type { ProductDisplay } from "@/lib/api/dutchie";
import { useListingScrollRestore } from "@/lib/hooks/use-listing-scroll";

/* ------------------------------------------------------------------
 🔧  CONFIG & THEME HELPERS
-------------------------------------------------------------------*/
export interface CategoryConfig {
  title: string;
  blurb?: string;
  fetch: () => Promise<ProductDisplay[]>;
  filterInStock?: boolean;
  virtualSubcategory?: (p: ProductDisplay) => string;
  effects?: string[];
  dosageRanges?: { label: string; test: (d: number) => boolean }[];
  weightRanges?: { label: string; extract: (p: ProductDisplay) => string[] }[];
  potencyUnit?: string;
  showPotency?: boolean;
  potencyFilterMode?: "thc" | "cbd" | "both";
  showWeight?: boolean;
  extraFilterChips?: (p: ProductDisplay) => Record<string, string[]>;
  headerTheme?: ThemeKey;
}
export interface CategoryLayoutProps {
  config: CategoryConfig;
  productCardProps?: Partial<ComponentPropsWithoutRef<typeof ProductCard>>;
}

/* ————————————————————————————————— theme util ————————————————————————————————— */
const theme = {
  purple: {
    gradient: "from-purple-800 via-purple-700 to-purple-600",
    backBtn: "bg-purple-900/30",
    text: "text-purple-100",
    activeBtn: "bg-purple-600 text-white",
    activeChip: "bg-purple-600 text-white",
    badge: "bg-purple-100 text-purple-800",
    primary: "bg-purple-600 hover:bg-purple-700 text-white",
  },
  green: {
    gradient: "from-green-800 via-green-700 to-green-600",
    backBtn: "bg-green-900/30",
    text: "text-green-100",
    activeBtn: "bg-green-600 text-white",
    activeChip: "bg-green-600 text-white",
    badge: "bg-green-100 text-green-800",
    primary: "bg-green-600 hover:bg-green-700 text-white",
  },
  blue: {
    gradient: "from-blue-800 via-blue-700 to-blue-600",
    backBtn: "bg-blue-900/30",
    text: "text-blue-100",
    activeBtn: "bg-blue-600 text-white",
    activeChip: "bg-blue-600 text-white",
    badge: "bg-blue-100 text-blue-800",
    primary: "bg-blue-600 hover:bg-blue-700 text-white",
  },
  yellow: {
    gradient: "from-yellow-800 via-yellow-700 to-yellow-600",
    backBtn: "bg-yellow-900/30",
    text: "text-yellow-100",
    activeBtn: "bg-yellow-600 text-white",
    activeChip: "bg-yellow-600 text-white",
    badge: "bg-yellow-100 text-yellow-800",
    primary: "bg-yellow-600 hover:bg-yellow-700 text-white",
  },
  fuchsia: {
    gradient: "from-fuchsia-800 via-fuchsia-700 to-fuchsia-600",
    backBtn: "bg-fuchsia-900/30",
    text: "text-fuchsia-100",
    activeBtn: "bg-fuchsia-600 text-white",
    activeChip: "bg-fuchsia-600 text-white",
    badge: "bg-fuchsia-100 text-fuchsia-800",
    primary: "bg-fuchsia-600 hover:bg-fuchsia-700 text-white",
  },
  orange: {
    gradient: "from-orange-800 via-orange-700 to-orange-600",
    backBtn: "bg-orange-900/30",
    text: "text-orange-100",
    activeBtn: "bg-orange-600 text-white",
    activeChip: "bg-orange-600 text-white",
    badge: "bg-orange-100 text-orange-800",
    primary: "bg-orange-600 hover:bg-orange-700 text-white",
  },
  red: {
    gradient: "from-red-800 via-red-700 to-red-600",
    backBtn: "bg-red-900/30",
    text: "text-red-100",
    activeBtn: "bg-red-600 text-white",
    activeChip: "bg-red-600 text-white",
    badge: "bg-red-100 text-red-800",
    primary: "bg-red-600 hover:bg-red-700 text-white",
  },
  teal: {
    gradient: "from-teal-800 via-teal-700 to-teal-600",
    backBtn: "bg-teal-900/30",
    text: "text-teal-100",
    activeBtn: "bg-teal-600 text-white",
    activeChip: "bg-teal-600 text-white",
    badge: "bg-teal-100 text-teal-800",
    primary: "bg-teal-600 hover:bg-teal-700 text-white",
  },
} as const;
type ThemeKey = keyof typeof theme;
const t = (k: ThemeKey | undefined) => theme[k ?? "purple"];

/* ------------------------------------------------------------------
 🧮  UTILS (sorting, parsing, generic helpers)
-------------------------------------------------------------------*/
const safe = (n: number) => (Number.isFinite(n) ? n : 0);
const mapObj = (obj: Record<string, any>, val: any) =>
  Object.fromEntries(Object.keys(obj).map((k) => [k, val]));
const keysSelected = (o: Record<string, boolean>) =>
  Object.keys(o).filter((k) => o[k]);
const extractNumber = (txt?: string) =>
  txt?.match(/(\d+(?:\.\d+)?)/)?.[1] ? parseFloat(RegExp.$1) : undefined;

/* weight helpers (unchanged logic, compact impl) */
const DEFAULT_WEIGHT_SORT = [
  ".5g",
  ".7g",
  "1g",
  "1.5g",
  "2g",
  "3.5g",
  "7g",
  "14g",
  "28g",
];
const normalizeWeight = (w: string): string => {
  const m = w.toLowerCase().trim().match(/(\d+(?:\.\d+)?)(g|gram|oz|ounce|eighth|quarter|half|lb)?/);
  if (!m) return w;
  const v = parseFloat(m[1]);
  const u = m[2] || "g";
  if (/eighth/.test(u)) return "3.5g";
  if (/quarter/.test(u)) return "7g";
  if (/half/.test(u)) return "14g";
  if (/oz|ounce/.test(u)) return "28g";
  if (/lb/.test(u)) return `${v * 448}g`;
  return `${v}g`;
};
const extractWeights = (p: ProductDisplay) => {
  const ws = new Set<string>();
  const add = (opt?: string) => opt && ws.add(normalizeWeight(opt));
  add(p.option);
  p.variants?.forEach((v) => add(v.option));
  return [...ws];
};
const sortWeights = (o: Record<string, boolean>) =>
  Object.keys(o).sort((a, b) =>
    parseFloat(a) && parseFloat(b) ? parseFloat(a) - parseFloat(b) : a.localeCompare(b)
  );

/* strain categorisation */
const strainCat = (s = ""): string => {
  const n = s.toLowerCase().replace(/[_\s]+/g, " ");
  if (/sativa.*hybrid|hybrid.*sativa/.test(n)) return "Sativa Hybrid";
  if (/indica.*hybrid|hybrid.*indica/.test(n)) return "Indica Hybrid";
  if (/indica/.test(n)) return "Indica";
  if (/sativa/.test(n)) return "Sativa";
  if (/hybrid/.test(n)) return "Hybrid";
  return "Other";
};

/* ------------------------------------------------------------------
 📦  MAIN COMPONENT
-------------------------------------------------------------------*/
export default function CategoryLayout({
  config,
  productCardProps = {},
}: CategoryLayoutProps) {
  /* ----------------------- state ----------------------- */
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState<ProductDisplay[]>([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("brand"); // Default to brand grouping
  const [view, setView] = useState<"grid" | "list">("grid");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  // Initialise page from ?page= so back-button navigation and deep links
  // land on the right page.
  const [page, setPage] = useState(() => {
    if (typeof window === "undefined") return 1;
    const p = new URLSearchParams(window.location.search).get("page");
    const n = p ? parseInt(p, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 1;
  });
  const PER_PAGE = 12;

  /* ---------------------- data ------------------------- */
  useEffect(() => {
    (async () => {
      try {
        setProducts(await config.fetch());
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---- scroll + page restore on back-nav (shared hook) ---- */
  useListingScrollRestore(!loading, page);

  /* --------------------- filters init ------------------ */
  const [filters, setFilters] = useState(() => ({
    sub: {},
    brand: {},
    eff: {},
    dose: {},
    weight: {},
    strain: {},
    pot: { thc: { val: [0, 0], min: 0, max: 0 }, cbd: { val: [0, 0], min: 0, max: 0 } },
  }));

  useEffect(() => {
    if (!products.length) return;
    const sub: any = {},
      brand: any = {},
      eff: any = {},
      dose: any = {},
      weight: any = {},
      strain: any = {};
    let [thcMin, thcMax, cbdMin, cbdMax] = [100, 0, 100, 0];
    config.dosageRanges?.forEach((r) => (dose[r.label] = false));

    products.forEach((p) => {
      // subcat
      const subKey = (p as any).virtualSubcategory
        ? (p as any).virtualSubcategory
        : config.virtualSubcategory?.(p) ?? p.subcategory;
      if (subKey) sub[subKey] = false;
      // brand
      if (p.brand) brand[p.brand] = false;
      // effects
      (config.effects ?? []).forEach((e) => {
        if (new RegExp(`\\b${e}\\b`, "i").test(`${p.name} ${p.description}`)) eff[e] = false;
      });
      // weight
      if (config.showWeight && config.weightRanges) {
        config.weightRanges.forEach((r) =>
          r.extract(p).forEach((w) => (weight[w] = false))
        );
      }
      // strain
      if (p.strainType) strain[strainCat(p.strainType)] = false;
      // potency
      const thc = extractNumber(p.thc);
      const cbd = extractNumber(p.cbd);
      if (thc !== undefined) {
        thcMin = Math.min(thcMin, thc);
        thcMax = Math.max(thcMax, thc);
      }
      if (cbd !== undefined) {
        cbdMin = Math.min(cbdMin, cbd);
        cbdMax = Math.max(cbdMax, cbd);
      }
    });
    setFilters({
      sub,
      brand,
      eff,
      dose,
      weight,
      strain,
      pot: {
        thc: { min: safe(thcMin), max: safe(thcMax), val: [safe(thcMin), safe(thcMax)] },
        cbd: { min: safe(cbdMin), max: safe(cbdMax), val: [safe(cbdMin), safe(cbdMax)] },
      },
    });
  }, [products, config]);

  /* ------------------- filter helpers ------------------ */
  const toggle = useCallback(
    (c: keyof typeof filters, k: string) =>
      setFilters((f: any) => ({ ...f, [c]: { ...f[c], [k]: !f[c][k] } })),
    []
  );
  const setPot = useCallback(
    (k: "thc" | "cbd", v: [number, number]) =>
      setFilters((f: any) => ({ ...f, pot: { ...f.pot, [k]: { ...f.pot[k], val: v } } })),
    []
  );
  const clearAll = () => {
    setFilters((f: any) => ({
      sub: mapObj(f.sub, false),
      brand: mapObj(f.brand, false),
      eff: mapObj(f.eff, false),
      dose: mapObj(f.dose, false),
      weight: mapObj(f.weight, false),
      strain: mapObj(f.strain, false),
      pot: {
        thc: { ...f.pot.thc, val: [f.pot.thc.min, f.pot.thc.max] },
        cbd: { ...f.pot.cbd, val: [f.pot.cbd.min, f.pot.cbd.max] },
      },
    }));
    setSearch("");
  };

  /* -------------------- filtered list ------------------ */
  const filtered = useMemo(() => {
    const sel = {
      sub: keysSelected(filters.sub),
      brand: keysSelected(filters.brand),
      eff: keysSelected(filters.eff).map((e) => e.toLowerCase()),
      dose: keysSelected(filters.dose),
      weight: keysSelected(filters.weight),
      strain: keysSelected(filters.strain),
    };
    const [minT, maxT] = filters.pot.thc.val;
    const [minC, maxC] = filters.pot.cbd.val;

    const potActive =
      filters.pot.thc.val[0] !== filters.pot.thc.min ||
      filters.pot.thc.val[1] !== filters.pot.thc.max ||
      filters.pot.cbd.val[0] !== filters.pot.cbd.min ||
      filters.pot.cbd.val[1] !== filters.pot.cbd.max;

    const hits = products.filter((p) => {
      if (search && !`${p.name} ${p.brand} ${p.description}`.toLowerCase().includes(search.toLowerCase()))
        return false;
      /* subcat */
      if (sel.sub.length) {
        const label = (
          (p as any).virtualSubcategory ||
          config.virtualSubcategory?.(p) ||
          p.subcategory ||
          ""
        ).toLowerCase();
        if (!sel.sub.some((s) => s.toLowerCase() === label)) return false;
      }
      /* brand */
      if (sel.brand.length && !sel.brand.includes(p.brand ?? "")) return false;
      /* effects */
      if (sel.eff.length && !sel.eff.some((e) => new RegExp(`\\b${e}\\b`, "i").test(`${p.name} ${p.description}`)))
        return false;
      /* dosage */
      if (sel.dose.length && config.dosageRanges && (p as any).dosage) {
        if (!sel.dose.some((l) => config.dosageRanges!.find((r) => r.label === l)?.test((p as any).dosage)))
          return false;
      }
      /* weight */
      if (sel.weight.length && config.showWeight && config.weightRanges) {
        const productWeights: string[] = [];
        config.weightRanges.forEach((r) => productWeights.push(...r.extract(p)));
        if (!productWeights.some((w) => sel.weight.includes(w))) return false;
      }
      /* strain */
      if (sel.strain.length && p.strainType && !sel.strain.includes(strainCat(p.strainType)))
        return false;
      /* potency */
      if (config.showPotency !== false && potActive) {
        const thc = extractNumber(p.thc) ?? 0;
        const cbd = extractNumber(p.cbd) ?? 0;
        if (thc < minT || thc > maxT || cbd < minC || cbd > maxC) return false;
      }
      return true;
    });

    return hits.sort((a, b) => {
      switch (sort) {
        case "price-asc":
          return (a.price ?? 0) - (b.price ?? 0);
        case "price-desc":
          return (b.price ?? 0) - (a.price ?? 0);
        case "name-asc":
          return (a.name ?? "").localeCompare(b.name ?? "");
        case "brand":
          // Group by brand alphabetically, then by name within brand
          const brandA = (a.brand ?? "ZZZ").toLowerCase();
          const brandB = (b.brand ?? "ZZZ").toLowerCase();
          if (brandA !== brandB) return brandA.localeCompare(brandB);
          return (a.name ?? "").localeCompare(b.name ?? "");
        default:
          return 0;
      }
    });
  }, [products, filters, search, sort, config]);

  const pages = Math.ceil(filtered.length / PER_PAGE);
  const current = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const activeCount =
    [filters.sub, filters.brand, filters.eff, filters.dose, filters.weight, filters.strain]
      .flatMap(Object.values)
      .filter(Boolean).length +
    (filters.pot.thc.val[0] !== filters.pot.thc.min || filters.pot.thc.val[1] !== filters.pot.thc.max ? 1 : 0) +
    (filters.pot.cbd.val[0] !== filters.pot.cbd.min || filters.pot.cbd.val[1] !== filters.pot.cbd.max ? 1 : 0);

  /* ------------------ render --------------------------- */
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="flex flex-col items-center">
        <div className="h-14 w-14 bg-gradient-to-br from-primary_green/20 to-emerald-200/40 rounded-2xl mb-4 animate-pulse"></div>
        <div className="h-4 w-36 bg-gray-200 rounded-full animate-pulse"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER - Compact on mobile */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-3 lg:py-5">
          {/* Mobile Header */}
          <div className="lg:hidden flex items-center gap-3">
            <Link href="/shop" className="p-2 -ml-2 rounded-lg text-gray-500 hover:text-primary_green hover:bg-gray-50 transition-colors">
              <ChevronLeft className="h-5 w-5" />
            </Link>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-gray-900">{config.title}</h1>
              <p className="text-xs text-gray-500">{filtered.length} products</p>
            </div>
          </div>
          
          {/* Desktop Header */}
          <div className="hidden lg:block">
            <Link href="/shop" className="inline-flex items-center gap-2 text-gray-500 hover:text-primary_green text-sm font-medium transition-colors mb-3">
              <ChevronLeft className="h-4 w-4" /> Back to Shop
            </Link>
            <div className="flex items-center gap-3">
              <div className={`w-1.5 h-8 rounded-full bg-gradient-to-b ${t(config.headerTheme).gradient}`}></div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{config.title}</h1>
                {config.blurb && <p className="text-gray-500 mt-1 text-sm">{config.blurb}</p>}
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-500">
              <span className="font-medium text-gray-700">{filtered.length}</span> products available
            </p>
          </div>
        </div>
      </header>
      {/* BODY */}
      <div className="container mx-auto py-3 lg:py-6 px-3 lg:px-4">
        <MobileTopBar {...{ search, setSearch, view, setView, activeCount }} openFilters={() => setShowMobileFilters(true)} theme={config.headerTheme} />
        <div className="flex flex-col lg:flex-row gap-6">
          <DesktopSidebar {...{ search, setSearch, activeCount, clearAll, filters, toggle, setPot }} potencyUnit={config.potencyUnit ?? "%"} showPotency={config.showPotency !== false} config={config} />
          <main className="flex-1 min-w-0">
            <DesktopTopBar total={filtered.length} {...{ sort, setSort, view, setView }} theme={config.headerTheme} show={!loading} />
            {activeCount > 0 && <ActiveChips filters={filters} toggle={toggle} theme={config.headerTheme} />}
            {current.length === 0 ? (
              <EmptyState onClear={clearAll} />
            ) : view === "grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {current.map((p) => (
                  <ProductCard key={p.id} product={p} {...productCardProps} />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {current.map((p) => (
                  <ProductCard key={p.id} product={p} variant="list" {...productCardProps} />
                ))}
              </div>
            )}
            {pages > 1 && <Pagination page={page} pages={pages} setPage={setPage} />}
          </main>
        </div>
      </div>

      {/* MOBILE FILTERS */}
      {showMobileFilters && (
        <MobileFilterDrawer {...{ filters, toggle, setPot, close: () => setShowMobileFilters(false), activeCount, clear: clearAll }} potencyUnit={config.potencyUnit ?? "%"} showPotency={config.showPotency !== false} config={config} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
 🔹  SMALL PRESENTATIONAL COMPONENTS (unchanged APIs, shorter bodies)
-------------------------------------------------------------------*/
const MobileTopBar = ({ search, setSearch, view, setView, activeCount, openFilters, theme }: any) => {
  const [searchOpen, setSearchOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (searchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchOpen]);
  
  return (
    <div className="lg:hidden mb-3">
      <div className="flex gap-2 items-center">
        {/* Expandable Search */}
        <div className={`transition-all duration-200 ease-out ${searchOpen ? 'flex-1' : 'flex-shrink-0'}`}>
          {searchOpen ? (
            <div className="relative">
              <input 
                ref={inputRef}
                value={search} 
                onChange={(e) => setSearch(e.target.value)} 
                placeholder="Search..." 
                className="w-full border border-gray-200 rounded-xl py-2.5 pl-9 pr-9 text-sm bg-white"
                onBlur={() => !search && setTimeout(() => setSearchOpen(false), 150)}
              />
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <button onClick={() => { setSearch(""); setSearchOpen(false); }} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                <X className="h-4 w-4 text-gray-400" />
              </button>
            </div>
          ) : (
            <button 
              onClick={() => setSearchOpen(true)} 
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors text-sm"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <span>Search</span>
            </button>
          )}
        </div>
        
        {/* Filter button with label */}
        <button onClick={openFilters} className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${activeCount ? t(theme).activeBtn : 'bg-gray-100 text-gray-600'}`}>
          <FilterIcon className="h-4 w-4" />
          <span>Filter</span>
          {activeCount > 0 && <span className="bg-white/20 rounded-full px-1.5 text-xs">{activeCount}</span>}
        </button>
        
        {/* View toggle with clearer buttons */}
        <div className="flex-shrink-0 flex bg-gray-100 rounded-xl overflow-hidden">
          <button 
            onClick={() => setView("grid")} 
            className={`px-3 py-2.5 transition-colors ${view === "grid" ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400'}`}
            aria-label="Grid view"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h7v7H3V3zm0 11h7v7H3v-7zm11-11h7v7h-7V3zm0 11h7v7h-7v-7z" /></svg>
          </button>
          <button 
            onClick={() => setView("list")} 
            className={`px-3 py-2.5 transition-colors ${view === "list" ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400'}`}
            aria-label="List view"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z" /></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

const DesktopSidebar = ({ search, setSearch, activeCount, clearAll, filters, toggle, showPotency, setPot, potencyUnit, config }: any) => (
  <aside className="hidden lg:block w-64 flex-shrink-0 sticky top-20 self-start bg-white border border-gray-100 rounded-2xl p-5 shadow-sm max-h-[calc(100vh-6rem)] overflow-y-auto">
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-lg font-semibold">Filters</h3>
      {activeCount > 0 && (
        <Button variant="outline" size="sm" onClick={clearAll}>
          <X className="h-3 w-3 mr-1" /> Reset ({activeCount})
        </Button>
      )}
    </div>
    <div className="relative mb-6">
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="w-full border rounded-xl py-3 pl-10 pr-4" />
      <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
    </div>
    {showPotency && ["thc", "cbd"].map((tKey) => (
      <div key={tKey} className="mb-6">
        <h4 className="font-medium mb-3">{tKey.toUpperCase()} ({potencyUnit})</h4>
        <Slider min={filters.pot[tKey].min} max={filters.pot[tKey].max} value={filters.pot[tKey].val} onValueChange={(v) => setPot(tKey, v)} />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{filters.pot[tKey].val[0]}{potencyUnit}</span><span>{filters.pot[tKey].val[1]}{potencyUnit}</span>
        </div>
      </div>
    ))}
    <Accordion type="multiple">
      {Object.keys(filters.sub).length > 0 && (
        <FilterSection label="Types">{Object.keys(filters.sub).sort().map((k) => <FilterButton key={k} theme={config.headerTheme} active={filters.sub[k]} onClick={() => toggle("sub", k)}>{k}</FilterButton>)}</FilterSection>) }
      {Object.keys(filters.dose).length > 0 && (
        <FilterSection label="Dosage">{Object.entries(filters.dose).map(([k, v]) => <FilterButton key={k} theme={config.headerTheme} active={v} onClick={() => toggle("dose", k)}>{k}</FilterButton>)}</FilterSection>)}
      {Object.keys(filters.eff).length > 0 && (
        <FilterSection label="Effects">{Object.entries(filters.eff).map(([k, v]) => <FilterButton key={k} theme={config.headerTheme} active={v} onClick={() => toggle("eff", k)}>{k}</FilterButton>)}</FilterSection>)}
      {Object.keys(filters.brand).length > 0 && (
        <FilterSection label="Brands">{Object.keys(filters.brand).sort().map((k) => <FilterButton key={k} theme={config.headerTheme} active={filters.brand[k]} onClick={() => toggle("brand", k)}>{k}</FilterButton>)}</FilterSection>)}
      {config.showWeight && Object.keys(filters.weight).length > 0 && (
        <FilterSection label="Weights">{sortWeights(filters.weight).map((k) => <FilterButton key={k} theme={config.headerTheme} active={filters.weight[k]} onClick={() => toggle("weight", k)}>{k}</FilterButton>)}</FilterSection>)}
      {Object.keys(filters.strain).length > 0 && (
        <FilterSection label="Strain Types">{Object.keys(filters.strain).sort().map((k) => <FilterButton key={k} theme={config.headerTheme} active={filters.strain[k]} onClick={() => toggle("strain", k)}>{k}</FilterButton>)}</FilterSection>)}
    </Accordion>
  </aside>
);

const DesktopTopBar = ({ total, sort, setSort, view, setView, show, theme }: any) =>
  show ? (
    <div className="hidden lg:flex justify-between items-center mb-6">
      <p className="text-sm text-gray-600">{total} products found</p>
      <div className="flex items-center gap-4">
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="border rounded px-3 py-2 text-sm">
          <option value="brand">Group by Brand</option>
          <option value="featured">Featured</option>
          <option value="price-asc">Price: Low to High</option>
          <option value="price-desc">Price: High to Low</option>
          <option value="name-asc">Name: A to Z</option>
        </select>
        <div className="flex border rounded overflow-hidden">
          {["grid", "list"].map((v) => (
            <button key={v} onClick={() => setView(v as any)} className={`p-2 ${view === v ? t(theme).activeBtn : "bg-white"}`}>
              {v === "grid" ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 3h7v7H3V3zm0 11h7v7H3v-7zm11-11h7v7h-7V3zm0 11h7v7h-7v-7z" /></svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 4h18v2H3V4zm0 7h18v2H3v-2zm0 7h18v2H3v-2z" /></svg>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  ) : null;

const ActiveChips = ({ filters, toggle, theme }: any) => (
  <div className="flex flex-wrap gap-2 mb-4">
    {Object.entries({ ...filters.sub, ...filters.brand, ...filters.eff, ...filters.dose })
      .filter(([, v]) => v)
      .map(([k]) => (
        <Badge key={k} className={`${t(theme).badge} flex items-center`}>
          {k}
          <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => {
            if (k in filters.sub) toggle("sub", k);
            else if (k in filters.brand) toggle("brand", k);
            else if (k in filters.eff) toggle("eff", k);
            else if (k in filters.dose) toggle("dose", k);
          }} />
        </Badge>
      ))}
  </div>
);

const FilterSection = ({ label, children }: { label: string; children: ReactNode }) => (
  <AccordionItem value={label}>
    <AccordionTrigger className="text-sm font-medium">{label}</AccordionTrigger>
    <AccordionContent>
      <div className="grid gap-2">{children}</div>
    </AccordionContent>
  </AccordionItem>
);
const FilterButton = ({ active, onClick, children, theme }: any) => (
  <button onClick={onClick} className={`p-2 rounded text-sm ${active ? t(theme).activeChip : "bg-gray-100"}`}>{children}</button>
);
const Pagination = ({ page, pages, setPage }: any) => (
  <div className="flex justify-center gap-2 mt-8">
    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p: number) => Math.max(1, p - 1))}>Prev</Button>
    {Array.from({ length: pages }, (_, i) => i + 1).map((i) => (
      <Button key={i} size="sm" variant={page === i ? "default" : "outline"} onClick={() => setPage(i)}>{i}</Button>
    ))}
    <Button variant="outline" size="sm" disabled={page === pages} onClick={() => setPage((p: number) => Math.min(pages, p + 1))}>Next</Button>
  </div>
);
const EmptyState = ({ onClear }: { onClear: () => void }) => (
  <div className="text-center py-12">
    <p className="text-gray-500 mb-4">No products match your filters.</p>
    <Button variant="outline" onClick={onClear}>Clear Filters</Button>
  </div>
);

// Brand Grouped Grid - shows products grouped by brand with headers
const BrandGroupedGrid = ({ products, productCardProps, view, theme }: { products: ProductDisplay[], productCardProps: any, view: "grid" | "list", theme?: ThemeKey }) => {
  // Group products by brand
  const grouped = products.reduce((acc, product) => {
    const brand = product.brand || "Other";
    if (!acc[brand]) acc[brand] = [];
    acc[brand].push(product);
    return acc;
  }, {} as Record<string, ProductDisplay[]>);
  
  // Sort brands alphabetically
  const sortedBrands = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
  
  return (
    <div className="space-y-8">
      {sortedBrands.map((brand) => (
        <div key={brand}>
          {/* Brand Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-1 h-6 rounded-full bg-gradient-to-b ${t(theme).gradient}`}></div>
            <h3 className="text-lg font-bold text-gray-900">{brand}</h3>
            <span className="text-sm text-gray-500">({grouped[brand].length})</span>
          </div>
          {/* Products Grid/List */}
          {view === "grid" ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {grouped[brand].map((p) => (
                <ProductCard key={p.id} product={p} {...productCardProps} />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {grouped[brand].map((p) => (
                <ProductCard key={p.id} product={p} variant="list" {...productCardProps} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
const MobileFilterDrawer = ({ filters, toggle, setPot, close, activeCount, clear, potencyUnit, showPotency, config }: any) => (
  <div className="fixed inset-0 z-50 lg:hidden">
    <div className="absolute inset-0 bg-black/30" onClick={close} />
    <div className="fixed bottom-0 inset-x-0 bg-white rounded-t-2xl shadow-lg max-h-[85vh] overflow-y-auto">
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold">Filters</h2>
        <div className="flex gap-3">
          <Button variant="outline" size="sm" disabled={activeCount === 0} onClick={clear}>Reset</Button>
          <Button size="sm" className={t(config.headerTheme).primary} onClick={close}>{activeCount ? `Apply (${activeCount})` : "Hide"}</Button>
        </div>
      </div>
      <div className="p-4 space-y-6">
        {showPotency && ["thc", "cbd"].map((tKey) => (
          <div key={tKey}>
            <h4 className="font-medium mb-3">{tKey.toUpperCase()} ({potencyUnit})</h4>
            <div className="flex justify-between text-sm mb-1"><span>{filters.pot[tKey].val[0]}{potencyUnit}</span><span>{filters.pot[tKey].val[1]}{potencyUnit}</span></div>
            <Slider min={filters.pot[tKey].min} max={filters.pot[tKey].max} value={filters.pot[tKey].val} onValueChange={(v) => setPot(tKey, v)} />
          </div>
        ))}
        {["sub", "dose", "eff", "brand", "strain", ...(config.showWeight ? ["weight"] : [])].map((cat) =>
          Object.keys(filters[cat]).length ? (
            <div key={cat}>
              <h4 className="font-medium mb-3">{cat.charAt(0).toUpperCase() + cat.slice(1)}</h4>
              <div className="grid gap-2 grid-cols-2">
                {Object.keys(filters[cat]).map((name) => (
                  <FilterButton key={name} theme={config.headerTheme} active={filters[cat][name]} onClick={() => toggle(cat, name)}>{name}</FilterButton>
                ))}
              </div>
            </div>
          ) : null
        )}
      </div>
    </div>
  </div>
);
